/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ClusterConnectorId, ClusterConnectorName } from './cluster3x';
import { PriceInCents } from './currency';
import { DrDcId } from './dr';
import { NebulaClusterInfo, TDEType } from './nebula';
import { TeamId } from './team';

export type ClusterId = string;
export type Cluster2xConnectionToken = string;

export type ClusterInfoResponse =
  | {
      id: ClusterId;
      tag: string;
      edition: ProductEdition2x;
      versions: string[];
      serverNodes: number;
      connected: boolean;
      attached: boolean;
      priceId: string;
      priceInCents: PriceInCents;
      productName: string;
    }
  | {
      id: null | string;
      tag: null | string;
      edition: ProductEdition3x;
      versions: null | string[];
      serverNodes: number;
      connected: boolean;
      attached: boolean;
      init: boolean;
      priceId: string;
      priceInCents: PriceInCents;
      productName: string;
    };

export type ProductEdition2x =
  | ProductEdition.AI
  | ProductEdition.GGCE
  | ProductEdition.GGEE
  | ProductEdition.GGUE
  // null is Apache Ignite without version info support
  | null;
export type ProductEdition3x =
  | ProductEdition.AI3
  | ProductEdition.GG9
  | ProductEdition.GG9CE
  | ProductEdition.GG9EE
  | ProductEdition.GG9UE;

type ClusterDataEncryption = {
  tdeType?: TDEType;
};

type ClusterInfoCommon = {
  edition: ProductEdition2x | ProductEdition3x;
  id: ClusterId;
  tag: string;
  lastSeen: number;
  connected: boolean;
  features: ClusterFeatures[];
  versions: string[];
  teamIds: TeamId[];
  ownerEmail: string;
  status: ClusterStatus;
  encryption?: ClusterDataEncryption;
  nebula: NebulaClusterInfo | null; // null for non-Nebula clusters
};

type ClusterStatusCommon =
  | ClusterStatus.ACTIVE
  | ClusterStatus.DISCONNECTED
  | ClusterStatus.PROVISIONING
  | ClusterStatus.RESUMING
  | ClusterStatus.DESTROYING
  | ClusterStatus.DESTROYED
  | ClusterStatus.FAILED
  | ClusterStatus.SUSPENDED
  | ClusterStatus.SUSPENDING
  | ClusterStatus.LIMITED;

export type ClusterStatus2x = ClusterStatusCommon | ClusterStatus.INACTIVE;
export interface ClusterInfo2x extends ClusterInfoCommon {
  status: ClusterStatus2x;
  edition: ProductEdition2x;
  info: {
    active: boolean;
    baselineSize: number;
    clientNodes: number;
    serverNodes: number;
    drDcId: DrDcId | null;
    configuration: {
      baselineParameters: {
        autoAdjustAwaitingTime: number;
        autoAdjustEnabled: boolean | null;
      };
      persistenceEnabled: boolean;
      pointInTimeRecoveryEnabled: boolean;
      secure: boolean;
      snapshotsEnabled: boolean;
      // Tells whether tracingSpi is configured or not
      tracingConfigured: boolean;
      // the event driven service processor enabled
      managedDeploymentSpiEnabled?: boolean;
      eventDrivenServiceProcessorEnabled?: boolean;
      priorityQueueSpiEnabled?: boolean;
      metricsEnabled: boolean;
      tracingEnabled: boolean;
      computeEnabled: boolean;
      runningQueriesEnabled: boolean;
      drSender: boolean;
      drReceiver: boolean;
    };
  };
}

export type ClusterStatus3x = ClusterStatusCommon | ClusterStatus.UNINITIALIZED;

export interface ClusterInfo3x extends ClusterInfoCommon {
  status: ClusterStatus3x;
  edition: ProductEdition3x;
  clusterId: string | null;
  info: (
    | {
        encryptionEnabled: true;
        encryptionActiveProvider: string;
      }
    | {
        encryptionEnabled: false;
        encryptionActiveProvider: null;
      }
    | {
        encryptionEnabled: undefined;
        encryptionActiveProvider: undefined;
      }
  ) & {
    metricsMonitoringEnabled: undefined | boolean;
    runningQueriesMonitoringEnabled: undefined | boolean;
    securityEnabled: undefined | boolean;
    licenseExpirationDate: undefined | string;
    connectorId?: ClusterConnectorId;
    connectorName?: ClusterConnectorName;
  };
}

export type ClusterInfo = ClusterInfo2x | ClusterInfo3x;

// control-center-agent/src/main/java/org/gridgain/control/agent/dto/cluster/ClusterEdition.java
export enum ProductEdition {
  AI = 'APACHE_IGNITE',
  GGCE = 'GRIDGAIN_COMMUNITY',
  GGEE = 'GRIDGAIN_ENTERPRISE',
  GGUE = 'GRIDGAIN_ULTIMATE',
  AI3 = 'APACHE_IGNITE_3', // Initialized or Uninitialized AI3
  GG9 = 'GRIDGAIN_9', // Uninitialized GG9
  GG9CE = 'GRIDGAIN_9_COMMUNITY',
  GG9EE = 'GRIDGAIN_9_ENTERPRISE',
  GG9UE = 'GRIDGAIN_9_ULTIMATE',
}

export const ProductEdition3xValues = [
  ProductEdition.AI3,
  ProductEdition.GG9,
  ProductEdition.GG9CE,
  ProductEdition.GG9EE,
  ProductEdition.GG9UE,
] as const;

export const ProductEditionGridGain2xValues = [ProductEdition.GGCE, ProductEdition.GGEE, ProductEdition.GGUE] as const;
export const ProductEditionGridGain3xValues = [
  ProductEdition.GG9CE,
  ProductEdition.GG9EE,
  ProductEdition.GG9UE,
  ProductEdition.GG9,
] as const;

export enum ClusterFeatures {
  BASELINE_AUTO_ADJUST = 'BASELINE_AUTO_ADJUSTMENT',
  GRIDGAIN_SNAPSHOTS = 'GRIDGAIN_SNAPSHOTS',
  GRIDGAIN_PITR = 'GRIDGAIN_PITR',
  GRIDGAIN_ROLLING_UPGRADES = 'GRIDGAIN_ROLLING_UPGRADES',
  GRIDGAIN_DR = 'GRIDGAIN_DR',
  TRACING_LIBS = 'TRACING',
  TRACING = 'TRACING_SUPPORTED',
  TRACING_DYNAMIC_CONFIGURATION = 'TRACING_DYNAMIC_CONFIGURATION',
  CACHE_ENDPOINT_AVAILABLE = 'CACHE_ENDPOINT_AVAILABLE_FEATURE',
  CACHE_STATISTICS_FEATURE = 'CACHE_STATISTICS_FEATURE',
  NODE_CONFIGURATION_AVAILABLE = 'NODE_CONFIGURATION_FEATURE',
  CODE_DEPLOYMENT = 'CODE_DEPLOYMENT',
  CHANGE_TAG = 'CLUSTER_CHANGE_TAG_FEATURE',
  COMPUTE_GRID_MONITORING = 'COMPUTE_GRID_MONITORING',
  RUNNING_QUERIES_MONITORING = 'RUNNING_QUERIES_MONITORING',
  COLLECT_SNAPSHOT_LIST = 'COLLECT_SNAPSHOT_LIST',
  AWS_KMS_TDE_SUPPORTED = 'AWS_KMS_TDE_SUPPORTED',
  TRIGGER_GC_SUPPORTED = 'TRIGGER_GC_SUPPORTED',
  LOGS_COLLECTION_SUPPORTED = 'LOGS_COLLECTION_SUPPORTED',
  THREAD_DUMP_SUPPORTED = 'THREAD_DUMP_SUPPORTED',
  BINARY_TYPE_MANAGEMENT = 'BINARY_TYPE_MANAGEMENT',
  DATA_REPLICATION = 'DATA_REPLICATION',
  DATA_REPLICATION_MONITORING = 'DATA_REPLICATION_MONITORING',
  SCHEDULE_SNAPSHOT = 'SCHEDULE_SNAPSHOT',
  GRIDGAIN_SNAPSHOT_OPERATIONS = 'GRIDGAIN_SNAPSHOT_OPERATIONS',
  SCAN_QUERIES = 'SCAN_QUERIES',
  LICENSE_UPLOAD = 'LICENSE_UPLOAD',
  RUNNING_QUERY_CONFIGURATION = 'RUNNING_QUERY_CONFIGURATION',
  BINARY_TYPE_WITH_AFFINITY_KEY = 'BINARY_TYPE_WITH_AFFINITY_KEY',
  SNAPSHOTS_3X = 'SNAPSHOTS_3X',
}

export type ClusterCredentials =
  | { login: string; password: string; useAccessToken: false }
  | { login: null; password: null; useAccessToken: true };

export type AttachClusterRequestBody = { credentials?: ClusterCredentials; teamId?: TeamId };

export type ChangeTagActionArgument = { tag: string };

export type ClusterUserName2x = string;

export type ClusterUserPassword2x = string;

export type ListClusterUsersActionResponse = ClusterUserName2x[];

export type AddClusterUserActionArgument = { login: ClusterUserName2x; password: ClusterUserPassword2x };

export type RemoveClusterUserActionArgument = ClusterUserName2x;

export enum ClusterStatus {
  PROVISIONING = 'PROVISIONING',
  RESUMING = 'RESUMING',
  DESTROYING = 'DESTROYING',
  DESTROYED = 'DESTROYED',
  FAILED = 'FAILED',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DISCONNECTED = 'DISCONNECTED',
  SUSPENDED = 'SUSPENDED',
  SUSPENDING = 'SUSPENDING',
  LIMITED = 'LIMITED',
  UNINITIALIZED = 'UNINITIALIZED',
}

export const ALL_CLUSTER_STATUSES: ClusterStatus[] = [
  ClusterStatus.PROVISIONING,
  ClusterStatus.RESUMING,
  ClusterStatus.DESTROYING,
  ClusterStatus.DESTROYED,
  ClusterStatus.FAILED,
  ClusterStatus.ACTIVE,
  ClusterStatus.INACTIVE,
  ClusterStatus.DISCONNECTED,
  ClusterStatus.SUSPENDED,
  ClusterStatus.SUSPENDING,
  ClusterStatus.LIMITED,
  ClusterStatus.UNINITIALIZED,
];

const COMMON_CLUSTER_STATUSES: ClusterStatusCommon[] = [
  ClusterStatus.ACTIVE,
  ClusterStatus.DISCONNECTED,
  ClusterStatus.PROVISIONING,
  ClusterStatus.RESUMING,
  ClusterStatus.DESTROYING,
  ClusterStatus.DESTROYED,
  ClusterStatus.FAILED,
  ClusterStatus.SUSPENDED,
  ClusterStatus.SUSPENDING,
  ClusterStatus.LIMITED,
];

export const ALL_CLUSTER_STATUSES_2x: ClusterStatus2x[] = [...COMMON_CLUSTER_STATUSES, ClusterStatus.INACTIVE];

export const ALL_CLUSTER_STATUSES_3x: ClusterStatus3x[] = [...COMMON_CLUSTER_STATUSES, ClusterStatus.UNINITIALIZED];

export const ACTIVE_STATUSES_3x: ClusterStatus3x[] = [ClusterStatus.ACTIVE, ClusterStatus.LIMITED];

export type ToggleClusterFeaturesBody = {
  tracing: { enabled: boolean };
  compute: { enabled: boolean };
  metrics: { enabled: boolean };
  runningQueries: { enabled: boolean };
};

export type ToggleClusterFeature = 'metrics' | 'compute' | 'tracing' | 'runningQueries';

export type ShareClusterRequestBody = { names: string[] };
export type ShareClusterResponse = { failed: { id: string; reason: string }[] };

export enum ClusterCondition {
  GOOD = 'GOOD',
  WARNING = 'WARNING',
  BAD = 'BAD',
}

export enum ClusterHealthDetailNameKnown {
  PARTITION_MAP_EXCHANGE = 'PartitionMapExchange',
  PARTITION_LOSS = 'PartitionLoss',
  ALERTS = 'Alerts',
}

export type ClusterHealthDetailName = ClusterHealthDetailNameKnown | string;

export type ClusterHealthDetail = {
  name: ClusterHealthDetailName;
  description: string | null;
  condition: ClusterCondition;
};

export type ClusterHealthResponseBody = ClusterHealthDetail[];

export type ChangeTdeMasterKeyArgument = string;

export type ClusterActionExportLogsResponse = {
  resourceId: string;
};
